import { buildGapScene, minuteToPercent, segmentSteps } from "../../utils/dispatchGeotabGap.js";
import { fmtTime } from "../geotab/geotabFormat.js";

// Lecteur d'un écart Geotab : la ligne du camion telle qu'au Gantt (copie de sa piste) et, dessous, sa journée Geotab sur la
// même échelle d'heures. La relecture s'arrête sur chaque différence avec le Prévu : arrêt prévu pas fait (hachuré, ✕
// imprimé), arrêt en plus (une copie glisse le long d'un pointillé qui avance jusqu'à sa place dans le Prévu). Dès la
// première différence, ce qui concorde pâlit en gardant ses couleurs.

const REPLAY_MS = 8000;
const HOLD_MS = { missed: 3200, extra: 4800 };
const LABEL_W = 130;
const PREVU_Y = 50;
const BETWEEN = 76;
const ADDED = { none: "aucune commande", unassigned: "commande sans tournée", pm: "Chargé PM non prévu" };
const NS = "http://www.w3.org/2000/svg";

const el = (tag, className, text) => { const node = document.createElement(tag); if (className) node.className = className; if (text != null) node.textContent = text; return node; };
const svgEl = (tag, attrs, parent) => { const node = document.createElementNS(NS, tag); Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, v)); parent?.append(node); return node; };
const place = (parent, node, x, y, w, h) => {
  Object.assign(node.style, { left: `${x}px`, top: `${y}px`, ...(w != null ? { width: `${w}px` } : {}), ...(h != null ? { height: `${h}px` } : {}) });
  parent.append(node);
  return node;
};
const ease = (p) => (p < 0.5 ? 2 * p * p : 1 - (-2 * p + 2) ** 2 / 2);

// Copie inerte du Gantt : seul le point bleu de l'étape en cours est conservé comme repère.
function cloneTrack(track) {
  const copy = track.cloneNode(true);
  copy.querySelectorAll('[data-geotab-real-skip],[data-geotab-real-line],[data-geotab-real-gap],[data-geotab-real-progress]:not([data-geotab-real-progress="now"])').forEach((node) => node.remove());
  copy.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"));
  copy.removeAttribute("data-fleet-timeline-track");
  copy.setAttribute("inert", "");
  copy.classList.add("gg-clone");
  copy.style.width = "100%";
  return copy;
}

export function mountGapPlayer(host, { item, day, track, scale, startAt = null, onTime, onEvent, onResume, onEnd }) {
  const scene = buildGapScene(day, item);
  const events = scene.events;
  const anims = new Set();
  // Horloge des arrêts et des petites animations, figée pendant la pause.
  let pausedAt = null, pausedFor = 0;
  const clock = () => (pausedAt ?? performance.now()) - pausedFor;
  // Petites animations, menées à leur fin d'un coup quand on saute, glisse ou reprend.
  const anim = (ms, step, done, delay = 0) => {
    const a = { frame: 0 }, t0 = clock() + delay;
    const tick = () => {
      const now = clock();
      if (now < t0) { a.frame = requestAnimationFrame(tick); return; }
      const p = Math.min(1, (now - t0) / ms);
      step(p);
      if (p < 1) a.frame = requestAnimationFrame(tick); else { anims.delete(a); done?.(); }
    };
    a.stop = () => { cancelAnimationFrame(a.frame); anims.delete(a); step(1); done?.(); };
    anims.add(a);
    a.frame = requestAnimationFrame(tick);
  };
  const stopAnims = () => [...anims].forEach((a) => a.stop());

  host.replaceChildren();
  const width = host.clientWidth, L = { x: LABEL_W, w: Math.max(width - LABEL_W - 4, 200) };
  const Xt = (t) => L.x + (minuteToPercent(scale, t) / 100) * L.w;

  // Prévu : la piste du camion copiée, ou ce qui empêche de la montrer.
  const lane = place(host, el("div", "gg-lane"), L.x, PREVU_Y, L.w);
  if (track) lane.append(cloneTrack(track));
  else lane.append(el("div", "gg-alone", day.sites?.length ? "Ligne du camion masquée dans le Gantt (recherche ou filtre) : retirez-les pour comparer." : "Aucune tournée au Dispatch pour ce camion."));
  const laneH = lane.offsetHeight || 34, geoY = PREVU_Y + laneH + BETWEEN;
  host.style.height = `${geoY + 40 + 30}px`;
  place(host, el("div", "gg-tag gg-tag-plan", "PRÉVU"), 0, PREVU_Y + laneH / 2 - 12);
  place(host, el("div", "gg-tag", "GEOTAB"), 0, geoY + 8);
  const cMiss = place(host, el("div", "gg-count"), 0, PREVU_Y + laneH / 2 + 16), cExtra = place(host, el("div", "gg-count"), 0, geoY + 40);

  // Segments du Gantt dans la copie ; un site se reconnaît aux numéros d'étapes de sa pastille, sinon à son heure prévue.
  const hb = host.getBoundingClientRect();
  const gantt = track ? [...lane.querySelectorAll(".fleet-timeline-segment")].map((node) => {
    const r = node.getBoundingClientRect();
    return { x: r.left - hb.left, y: r.top - hb.top, w: r.width, h: r.height, steps: segmentSteps(node.innerText) };
  }) : [];
  const segOf = (site) => gantt.find((sg) => sg.steps && (site.steps || []).some((n) => n >= sg.steps[0] && n <= sg.steps[1]))
    || gantt.find((sg) => Xt(site.planArrival) + 1 >= sg.x && Xt(site.planArrival) + 1 <= sg.x + sg.w) || null;
  const prevuBottom = gantt.length ? Math.max(...gantt.map((sg) => sg.y + sg.h)) : PREVU_Y + laneH;

  // Ce qui concorde pâlit ; prévu pas fait : hachures, ✕ et raison au-dessus.
  const missedSegs = new Set();
  const missedUI = new Map(scene.missingGroups.map((group) => {
    const parts = group.sites.map(segOf).filter(Boolean).map((sg) => {
      missedSegs.add(sg);
      return { hatch: place(host, el("div", "gg-notdone"), sg.x, sg.y, sg.w, sg.h), x: place(host, el("div", "gg-miss", "✕"), sg.x + sg.w / 2, sg.y - 11), sg };
    });
    const cx = parts.length ? (parts[0].sg.x + parts[parts.length - 1].sg.x + parts[parts.length - 1].sg.w) / 2 : L.x;
    return [group, { parts, why: place(host, el("div", "gg-why", group.label), Math.max(L.x + 90, Math.min(cx, L.x + L.w - 140)), PREVU_Y - 26) }];
  }));
  const veils = gantt.filter((sg) => !missedSegs.has(sg)).map((sg) => place(host, el("div", "gg-veil-seg"), sg.x, sg.y - 14, sg.w, sg.h + 16));

  // Geotab, sur la même échelle d'heures.
  const strip = place(host, el("div", "gg-strip"), L.x, geoY, L.w);
  const segs = scene.segments.map((segment) => {
    const node = el("div", "gg-seg");
    node.append(el("span", null, segment.label));
    node.title = `${segment.label} · ${fmtTime(segment.from)} → ${fmtTime(segment.to)}`;
    Object.assign(node.dataset, { kind: segment.kind, shown: "false", extra: segment.extra ? "true" : "false" });
    const from = minuteToPercent(scale, segment.from), to = minuteToPercent(scale, segment.to);
    Object.assign(node.style, { left: `${from}%`, width: `calc(${Math.max(to - from, 0.6)}% - 1px)` });
    strip.append(node);
    return { segment, node, cx: Xt((segment.from + segment.to) / 2) };
  });

  // Liens gris entre ce qui concorde ; flèche pointillée de l'arrêt en plus vers sa place dans le Prévu.
  const svg = svgEl("svg", { class: "gg-links", width: 1, height: 1 }, host);
  const links = scene.matched.map((link) => {
    const sg = segOf(link), seg = segs.find((s) => s.segment.key === link.segmentKey);
    if (!sg || !seg) return null;
    const a = sg.x + sg.w / 2, b = seg.cx, mid = (prevuBottom + geoY) / 2;
    return { seg, path: svgEl("path", { class: "gg-link", d: `M${a},${prevuBottom + 2} C${a},${mid} ${b},${mid} ${b},${geoY - 2}` }, svg) };
  }).filter(Boolean);
  const extras = segs.filter((s) => s.segment.extra);
  const anchor = scene.caret.after ? segOf(scene.caret.after) : scene.caret.before ? segOf(scene.caret.before) : null;
  const caretX = anchor ? (scene.caret.after ? anchor.x + anchor.w : anchor.x) : null;
  let arrow = null;
  if (extras.length && caretX != null) {
    const sx = (Xt(extras[0].segment.from) + Xt(extras[extras.length - 1].segment.to)) / 2;
    const d = `M${sx},${geoY - 2} C${sx},${geoY - 52} ${caretX},${prevuBottom + 50} ${caretX},${prevuBottom + 12}`;
    const mask = svgEl("mask", { id: "gg-reveal", maskUnits: "userSpaceOnUse", x: -50, y: -50, width: width + 100, height: geoY + 200 }, svgEl("defs", {}, svg));
    const reveal = svgEl("path", { d, fill: "none", stroke: "#fff", "stroke-width": 18, "stroke-linecap": "round" }, mask);
    svgEl("path", { d, class: "gg-arrow-glow", mask: "url(#gg-reveal)" }, svg);
    const dash = svgEl("path", { d, class: "gg-arrow-dash", mask: "url(#gg-reveal)" }, svg);
    const len = dash.getTotalLength();
    reveal.style.strokeDasharray = `${len + 20}`;
    const end1 = dash.getPointAtLength(Math.max(len - 8, 0)), end2 = dash.getPointAtLength(len);
    const headG = svgEl("g", { transform: `translate(${end2.x},${end2.y + 4}) rotate(${(Math.atan2(end2.y - end1.y, end2.x - end1.x) * 180) / Math.PI})` }, svg);
    arrow = {
      sx, dash, len, reveal: (p) => { reveal.style.strokeDashoffset = `${(len + 20) * (1 - p)}`; },
      head: svgEl("path", { d: "M2,0 L-13,-8 L-13,8 z", class: "gg-arrow-head" }, headG),
      caret: place(host, el("div", "gg-caret"), caretX, anchor.y - 4, null, anchor.h + 8),
      pill: place(host, el("div", "gg-pill", `Ajouté par le chauffeur · ${ADDED[item.case] || ADDED.none}`), Math.min(caretX + 140, width - 150), (prevuBottom + geoY) / 2 + 8),
    };
  }
  const playhead = place(host, el("div", "gg-head"), 0, PREVU_Y - 6, null, geoY + 40 - PREVU_Y + 12);

  // État de la relecture à l'instant t ; pending : la différence en cours d'animation, pas encore montrée.
  const setMissed = (group, on) => {
    const ui = missedUI.get(group);
    ui.parts.forEach((p) => { p.hatch.style.opacity = on ? 1 : 0; p.x.classList.toggle("is-on", on); });
    ui.why.classList.toggle("is-on", on);
  };
  const setExtra = (on) => {
    if (!arrow) return;
    arrow.reveal(on ? 1 : 0); arrow.head.style.opacity = on ? 1 : 0;
    arrow.caret.classList.toggle("is-on", on); arrow.pill.classList.toggle("is-on", on);
  };
  const render = (t, pending = null) => {
    playhead.style.left = `${Xt(t)}px`; playhead.dataset.time = fmtTime(t);
    const pale = events.length > 0 && t >= events[0].t;
    segs.forEach((s) => { s.node.dataset.shown = s.segment.from <= t ? "true" : "false"; s.node.dataset.pale = pale && !s.segment.extra ? "true" : "false"; });
    links.forEach((link) => link.path.classList.toggle("is-on", link.seg.segment.from <= t));
    veils.forEach((veil) => veil.classList.toggle("is-on", pale));
    const done = (ev) => t >= ev.t && ev !== pending;
    events.forEach((ev) => (ev.kind === "extra" ? setExtra(done(ev)) : setMissed(ev.group, done(ev))));
    const nMiss = events.filter((ev) => ev.kind === "missed" && done(ev)).reduce((n, ev) => n + missedUI.get(ev.group).parts.length, 0);
    const nExtra = events.some((ev) => ev.kind === "extra" && done(ev)) ? 1 : 0;
    cMiss.textContent = `${nMiss} pas fait${nMiss > 1 ? "s" : ""}`; cMiss.classList.toggle("is-on", nMiss > 0);
    cExtra.textContent = `${nExtra} en plus`; cExtra.classList.toggle("is-on", nExtra > 0);
    onTime?.(t, pending, [start, end]);
  };

  // Animation d'une différence ; renvoie la durée de l'arrêt.
  const animate = (ev) => {
    if (ev.kind === "missed") {
      const ui = missedUI.get(ev.group);
      ui.parts.forEach((p) => anim(600, (q) => { p.hatch.style.opacity = q; }));
      anim(1, () => {}, () => { ui.parts.forEach((p) => { p.x.classList.remove("is-stamp"); void p.x.offsetWidth; p.x.classList.add("is-on", "is-stamp"); }); ui.why.classList.add("is-on"); }, 350);
      return HOLD_MS.missed;
    }
    extras.forEach((s) => { s.node.dataset.ring = "false"; void s.node.offsetWidth; s.node.dataset.ring = "true"; });
    if (!arrow) return HOLD_MS.extra;
    arrow.head.classList.remove("is-pop"); arrow.caret.classList.remove("is-pop");
    const land = () => { arrow.head.style.opacity = 1; void arrow.head.getBoundingClientRect(); arrow.head.classList.add("is-pop"); arrow.caret.classList.add("is-on", "is-pop"); arrow.pill.classList.add("is-on"); };
    // Une copie de l'arrêt Geotab glisse le long du pointillé et se pose à sa place dans le Prévu.
    const gw = Math.max(Xt(extras[extras.length - 1].segment.to) - Xt(extras[0].segment.from), 136);
    const ghost = place(host, el("div", "gg-ghost", extras.map((s) => s.segment.label).join(" → ")), 0, 0, gw);
    anim(3000, (q) => {
      const e = ease(q), pt = arrow.dash.getPointAtLength(arrow.len * e);
      arrow.reveal(e);
      ghost.style.transform = `translate(${pt.x - gw / 2}px, ${pt.y - 17}px) scale(${1 - 0.55 * e})`;
      ghost.style.opacity = q > 0.92 ? (1 - q) / 0.08 : 1;
    }, () => { ghost.remove(); land(); });
    return HOLD_MS.extra;
  };

  // Relecture de la journée en 8 s, avec un arrêt sur chaque différence.
  const start = day.real_departure ?? scene.segments[0]?.from ?? scale.start;
  const end = Math.max(scene.endAt ?? start, start + (day.in_progress ? 0 : 1));
  const p = { t: start, playing: false, hold: null, idx: 0, last: 0, done: false, frame: 0 };
  const finish = () => { p.done = true; p.playing = false; p.hold = null; render(end); playhead.hidden = !day.in_progress; onEnd?.(); };
  const release = () => { stopAnims(); p.hold = null; onResume?.(); };
  const step = (now) => {
    if (!p.playing) return;
    const dt = p.last ? now - p.last : 0;
    p.last = now;
    if (p.hold != null) {
      if (clock() >= p.hold) release(); else { p.frame = requestAnimationFrame(step); return; }
    }
    const ev = events[p.idx], t = Math.min(end, p.t + (dt / REPLAY_MS) * (end - start));
    if (ev && t >= ev.t) {
      p.t = ev.t; p.idx += 1; render(ev.t, ev);
      onEvent?.(ev);
      p.hold = clock() + animate(ev);
      p.frame = requestAnimationFrame(step);
      return;
    }
    p.t = t; render(t);
    if (t >= end) finish(); else p.frame = requestAnimationFrame(step);
  };
  const api = {
    range: [start, end],
    get playing() { return p.playing; },
    get holding() { return p.hold != null; },
    get t() { return p.t; },
    play() {
      if (pausedAt != null) { pausedFor += performance.now() - pausedAt; pausedAt = null; }
      if (p.done) { stopAnims(); onResume?.(); p.done = false; p.t = start; p.idx = 0; p.hold = null; playhead.hidden = false; render(start); }
      p.playing = true; p.last = 0; p.frame = requestAnimationFrame(step);
    },
    pause() { p.playing = false; cancelAnimationFrame(p.frame); pausedAt ??= performance.now(); },
    continue() { if (p.hold != null) release(); },
    seek(t) {
      stopAnims(); p.hold = null; onResume?.(); api.pause();
      p.t = Math.min(Math.max(t, start), end); p.done = false; playhead.hidden = false;
      p.idx = events.filter((ev) => ev.t < p.t).length;
      if (p.t >= end) { p.idx = events.length; finish(); } else render(p.t);
    },
    skip() { stopAnims(); p.hold = null; onResume?.(); api.pause(); p.t = end; p.idx = events.length; finish(); },
    destroy() { api.pause(); stopAnims(); host.replaceChildren(); },
  };
  if (startAt != null) api.seek(startAt); else render(start);
  return api;
}
